import WarpElement from "@warp-ds/elements-core";
import { css, html } from "lit";
import { TrackingEventMap, eventReference } from "../../models/trackingEvent.js";
import { trackClickEvent } from "../tracking.js";

type TabKey = "all" | "sell";

const TAB_EVENT_NAME: Record<TabKey, string> = {
    all: "All Cars Tab",
    sell: "Sell Tab",
};

/** Left-to-right order of the tabs, used for arrow-key navigation. */
const TAB_ORDER: readonly TabKey[] = ["all", "sell"];

class LandingTabs extends WarpElement {
    declare activeTab: TabKey;
    declare allLabel: string;
    declare sellLabel: string;
    declare trackingEventMap: TrackingEventMap;

    static properties = {
        activeTab: { type: String, attribute: "active-tab", reflect: true },
        allLabel: { type: String, attribute: "all-label" },
        sellLabel: { type: String, attribute: "sell-label" },
        trackingEventMap: { type: Object },
    };

    constructor() {
        super();
        this.activeTab = "all";
        this.allLabel = "All";
        this.sellLabel = "Sell";
    }

    static styles = [
        WarpElement.styles,
        css`
            :host {
                display: block;
            }
            @css-placeholder-ORUcf8;
            [role="tablist"] {
                position: sticky;
                top: 48px;
                z-index: 10;
                background-color: var(--w-s-color-background, #fff);
            }
            [role="tablist"]::before {
                content: "";
                position: absolute;
                top: 0;
                left: 50%;
                transform: translateX(-50%);
                width: 100vw;
                height: 100%;
                background-color: var(--w-s-color-background, #fff);
                z-index: -1;
            }
            @media (min-width: 768px) {
                [role="tab"] {
                    flex: 0 0 auto;
                    padding: 8px 12px;
                    text-align: left;
                }
            }
        `,
    ];

    #setTab(tab: TabKey) {
        this.activeTab = tab;
        this.#syncUrl(tab);
        const baseEvent = this.trackingEventMap?.[eventReference.TAB_CLICK];
        if (baseEvent) {
            trackClickEvent({ ...baseEvent, name: TAB_EVENT_NAME[tab] });
        }
    }

    /**
     * Keyboard support for the WAI-ARIA tabs pattern (roving tabindex). Arrow keys move
     * between tabs (with wraparound), Home/End jump to the first/last tab. The newly
     * selected tab is activated and receives focus, matching automatic-activation tabs.
     */
    #onKeydown(event: KeyboardEvent) {
        const current = TAB_ORDER.indexOf(this.activeTab);
        let next: number;
        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                next = (current + 1) % TAB_ORDER.length;
                break;
            case "ArrowLeft":
            case "ArrowUp":
                next = (current - 1 + TAB_ORDER.length) % TAB_ORDER.length;
                break;
            case "Home":
                next = 0;
                break;
            case "End":
                next = TAB_ORDER.length - 1;
                break;
            default:
                return;
        }
        event.preventDefault();
        const tab = TAB_ORDER[next];
        this.#setTab(tab);
        void this.updateComplete.then(() => {
            this.renderRoot.querySelector<HTMLButtonElement>(`#landing-tab-${tab}`)?.focus();
        });
    }

    /**
     * Keep the URL in sync with the active tab so it is shareable/bookmarkable and
     * consistent with the `?tab=` deep-link param (MPB-3525). The default tab ("all")
     * drops the param to keep canonical URLs clean. Uses replaceState so switching tabs
     * does not pollute the browser history.
     */
    #syncUrl(tab: TabKey) {
        if (typeof window === "undefined" || !window.history?.replaceState) return;
        const url = new URL(window.location.href);
        if (tab === "all") {
            url.searchParams.delete("tab");
        } else {
            url.searchParams.set("tab", tab);
        }
        window.history.replaceState(window.history.state, "", url);
    }

    render() {
        const isAll = this.activeTab === "all";
        const isSell = this.activeTab === "sell";
        const tabStyle = (active: boolean) =>
            `border: 0; border-bottom: 2px solid ${active ? "var(--w-s-color-link, #0063fb)" : "transparent"};`;

        return html`
            <div
                class="flex gap-24 border-b s-border mb-16"
                role="tablist"
                aria-label="Landing page sections"
                @keydown="${this.#onKeydown}"
            >
                <button
                    type="button"
                    role="tab"
                    id="landing-tab-all"
                    aria-selected="${isAll ? "true" : "false"}"
                    aria-controls="landing-panel-all"
                    tabindex="${isAll ? "0" : "-1"}"
                    class="flex-1 px-16 py-12 text-center text-body font-bold bg-transparent cursor-pointer ${isAll
                        ? "s-text-link"
                        : "s-text-subtle hover:s-text"}"
                    style="${tabStyle(isAll)}"
                    @click="${() => this.#setTab("all")}"
                >
                    ${this.allLabel}
                </button>
                <button
                    type="button"
                    role="tab"
                    id="landing-tab-sell"
                    aria-selected="${isSell ? "true" : "false"}"
                    aria-controls="landing-panel-sell"
                    tabindex="${isSell ? "0" : "-1"}"
                    class="flex-1 px-16 py-12 text-center text-body font-bold bg-transparent cursor-pointer ${isSell
                        ? "s-text-link"
                        : "s-text-subtle hover:s-text"}"
                    style="${tabStyle(isSell)}"
                    @click="${() => this.#setTab("sell")}"
                >
                    ${this.sellLabel}
                </button>
            </div>
            <div id="landing-panel-all" role="tabpanel" aria-labelledby="landing-tab-all" ?hidden="${!isAll}">
                <slot name="all"></slot>
            </div>
            <div id="landing-panel-sell" role="tabpanel" aria-labelledby="landing-tab-sell" ?hidden="${!isSell}">
                <slot name="sell"></slot>
            </div>
        `;
    }
}

customElements.define("landing-tabs", LandingTabs as unknown as CustomElementConstructor);
